/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { Observable, Subject } from 'rxjs';

import { INSERTION_POINT_TEXTURES_LABEL } from '@touchplan/strings';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { EventBinder } from '@appPlanApplication/interaction';

import { InsertionPointLabelContainer } from './InsertionPointLabelContainer';
import { INSERTION_POINT_GLOBAL_SETTINGS } from './InsertionPointConfiguration';
import { InsertionPointRectangle } from './InsertionPointRectangle';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { CursorType } from '@appCanvasCommon/events/cursor-type.enum';

/**
 * This class is responsible for creating and updating the child components of the Ticket Insertion Point as needed.
 * Additionally, it manages the registration of drag events and emits changes to the class instance.
 */
export interface InsertionPointContainerTextConfiguration {
	visible: boolean;
	width: number;
	y: number;
}

export interface InsertionPointContainerConfiguration {
	visible: boolean;
	width: number;
	y: number;
}

interface DragEvent {
	center: {
		y: number;
		x: number;
	};
	deltaY: number;
}

export class InsertionPointContainer extends FancyContainer {
	private _insertionPointArea: InsertionPointRectangle;
	private _insertionPointTopLine: InsertionPointRectangle;
	private _insertionPointLabelContainer: InsertionPointLabelContainer;
	private _binder = new EventBinder(this, 'hammer-panstart', 'hammer-panend');
	private _initialYPosition: number = null;
	private _destroySubject$: Subject<void> = new Subject();
	private _dragSubject$: Subject<number> = new Subject();
	public onDrag$: Observable<number> = this._dragSubject$.asObservable();

	constructor(initialConfiguration: InsertionPointContainerConfiguration) {
		super();
		this.init(initialConfiguration);
	}

	public update(configuration: InsertionPointContainerConfiguration): void {
		this.y = configuration.y;
		this.visible = configuration.visible;
		this.width = configuration.width;
		this._insertionPointArea.update({ width: configuration.width });
		this._insertionPointTopLine.update({ width: configuration.width });
		this._insertionPointLabelContainer.update({ x: configuration.width / 2 });
	}

	public override destroy(): void {
		this._binder.destroy();
		this._initialYPosition = null;
		this._insertionPointArea.destroy();
		this._insertionPointTopLine.destroy();
		this._insertionPointLabelContainer.destroy();
		this._dragSubject$.complete();
		this._destroySubject$.next();
		this._destroySubject$.complete();
		super.destroy();
	}

	private init(initialConfiguration: InsertionPointContainerConfiguration): void {
		// Static configuration
		this.x = 0;
		this.height = INSERTION_POINT_GLOBAL_SETTINGS.height;
		this.eventMode = EventMode.Static;
		this.cursor = CursorType.Pointer;
		// Dynamic configuration
		this.visible = initialConfiguration.visible;
		this.width = initialConfiguration.width;
		this.y = initialConfiguration.y;
		// Content
		this._insertionPointArea = new InsertionPointRectangle({
			width: initialConfiguration.width,
			height: INSERTION_POINT_GLOBAL_SETTINGS.height,
			tint: INSERTION_POINT_GLOBAL_SETTINGS.backgroundColor,
		});
		this._insertionPointTopLine = new InsertionPointRectangle({
			width: initialConfiguration.width,
			height: INSERTION_POINT_GLOBAL_SETTINGS.topLineHeight,
			tint: INSERTION_POINT_GLOBAL_SETTINGS.topLineBackgroundColor,
		});
		this._insertionPointLabelContainer = new InsertionPointLabelContainer(INSERTION_POINT_TEXTURES_LABEL, {
			x: initialConfiguration.width / 2,
		});
		this.addChild(this._insertionPointArea);
		this.addChild(this._insertionPointTopLine);
		this.addChild(this._insertionPointLabelContainer);
		this.bindDragEvents();
	}

	private bindDragEvents(): void {
		this._binder.add('hammer-panstart', (event: DragEvent) => {
			this._initialYPosition = event.center.y;
		});
		this._binder.add('hammer-pan', (event: DragEvent) => {
			const newScreenYPosition = this._initialYPosition + event.deltaY;
			this._dragSubject$.next(newScreenYPosition);
		});
	}
}
